import React from 'react';
import {Platform, StyleSheet, Text, View} from 'react-native';
import ScreenContext from '../../ScreenContext';
import NavActions from 'src/actions/NavActions';
import AppRoutes from 'src/AppRoutes';
import Events, {FundStatus} from 'src/logging/Events';
import FundingService from 'src/services/FundingService';
import Util, {
  rewardsCancelPolicyInBackground,
  rewardsStartPolicyInBackground,
} from 'src/Util';
import RoundedButton, {ButtonType} from '../../elements/RoundedButton';
import Styles from '../../Styles';
import CreditCardImage from '../CreditCardImage';
import PaymentMethodDropdown from '../PaymentMethodDropdown';
import AmountDropdown from './AmountDropdown';
import Localized from 'src/constants/AppStrings';
import {alertError} from '../../helpers/AlertHelper';
import AccountStore from 'src/stores/AccountStore';
import AccountConstants from 'src/constants/AccountConstants';
import {CreditCard} from 'src/models/CreditCard';
import {AppDispatch, RootState} from 'src/redux/store';
import {connect} from 'react-redux';
import {PaymentCredentials} from 'src/models/PaymentCredentials';
import {
  addFundsCard,
  addFundsFailed,
  IAddFundsCardParams,
} from '../../../redux/thunks/fundingThunks';
import AppRatingService from 'src/services/AppRatingService';
import Settings from 'src/Settings';
import AVTouchableOpacity from 'src/components/elements/AVTouchableOpacity';
import ChevronRight from 'src/components/img/svg/ChevronRight';
import {getDescriber} from './descriptor/DescriptorType';
import {BalanceTypes} from '../../../types/serverTypes/Account';
import FontAwesome5Pro from '../../icons/FontAwesomeIcon';
import {Balance} from 'src/models/Balance';

import {getDescriber as getEleDescriber} from 'src/components/elements/descriptor/DescriptorType';
import {getDescriber as getSnackDescriber} from 'src/components/screens/sendSnack/descriptor/sendasnack/DescriptorType';
import {
  adjustDefaultBalance,
  startPaymentFunding,
} from '../../../redux/slices/accountSlice';
import {
  withGlobalize,
  WithGlobalizeProps,
} from '../../../i18n/HOC/withGlobalize';
import {ErrorTypeEnum} from 'src/constants/ErrorTypeEnum';
import CrashlyticsEvents from 'src/logging/Crashlytics';
import {generateErrorMessage} from 'src/logging/generateErrorMessage';

type FundAccountTabProps = WithGlobalizeProps & {
  remainingDue: number;
  isSnackGiftDue: boolean;
  close: () => void;
  isHomeLocationSma: boolean;
  addFundsSuccess?: () => void;
  addFundsValidate?: () => Promise<ErrorTypeEnum>;
  addFundsFailed?: (errorType?: ErrorTypeEnum) => void;
  creditCards: Array<CreditCard>;
  payrollAvailable: boolean;
  paymentCredentials: PaymentCredentials;
  defaultPaymentMethodToken: string;
  payrollIdentifierId: string;
  transDate: string;
  addFundsCard(params: IAddFundsCardParams): Promise<unknown>;
  balances?: Array<Balance>;
  dispatch: AppDispatch;
  rewardProgramId?: string;
  rewardsMinLoadAmount?: number;
};

type FundAccountState = {
  amount: number;
  cardToken: string;
  issuer: string;
  autoFundOn: boolean;
  autoFundBelowAmount: number;
  autoFundAmount: number;
  paymentMenuVisible: boolean;
  amountMenuVisible: boolean;
};

class FundAccountTab extends React.Component<
  FundAccountTabProps,
  FundAccountState
> {
  static defaultProps = {
    remainingDue: 0,
    isSnackGiftDue: false,
  };
  static contextType = ScreenContext;
  declare context: React.ContextType<typeof ScreenContext>;

  constructor(props: FundAccountTabProps) {
    super(props);

    const creditCardsFiltered = Util.filterExpiredCreditCards(
      props.creditCards,
    );
    const getDefaultCard = creditCardsFiltered.filter((item) => item.isDefault);

    let token =
      getDefaultCard.length > 0
        ? getDefaultCard[0].id
        : props.defaultPaymentMethodToken;

    if (!token && props.paymentCredentials?.applePayAvailable) {
      token = AccountConstants.APPLE_PAY_TOKEN;
    } else if (!token && props.paymentCredentials?.googlePayAvailable) {
      token = AccountConstants.GOOGLE_PAY_TOKEN;
    } else if (!token && props.payrollAvailable) {
      token = AccountConstants.PAYROLL_TOKEN;
    }

    const issuer = AccountStore.getPaymentIssuer(token, creditCardsFiltered);
    let amount = AccountStore.getDefaultAmount();

    if (props.remainingDue > 1) {
      amount = props.remainingDue;
    }

    this.state = {
      amount,
      cardToken: token,
      issuer,
      autoFundOn: AccountStore.isAutoFundingOn(),
      autoFundAmount: AccountStore.getAutoFundAmount(),
      autoFundBelowAmount: AccountStore.getAutoFundFallBelowAmount(),
      paymentMenuVisible: false,
      amountMenuVisible: false,
    };

    this.formatCurrency = this.formatCurrency.bind(this);
    this.amountSelected = this.amountSelected.bind(this);
    this.onAccountStoreChanged = this.onAccountStoreChanged.bind(this);
    this.cardSelected = this.cardSelected.bind(this);
    this.addClicked = this.addClicked.bind(this);
    this.validate = this.validate.bind(this);
    this.renderOldAutoReloadSection =
      this.renderOldAutoReloadSection.bind(this);
    this.addFundsSuccess = this.addFundsSuccess.bind(this);
    this.checkisRefive = this.checkisRefive.bind(this);
    this.hasSameCurrencyAsPayroll = this.hasSameCurrencyAsPayroll.bind(this);
  }

  componentDidMount() {
    AccountStore.addChangeListener(this.onAccountStoreChanged);
  }

  componentDidUpdate(prevProps: FundAccountTabProps) {
    if (
      prevProps.defaultPaymentMethodToken !==
        this.props.defaultPaymentMethodToken &&
      this.props.defaultPaymentMethodToken &&
      !this.state.cardToken
    ) {
      const creditCardsFiltered = Util.filterExpiredCreditCards(
        this.props.creditCards,
      );
      const newIssuer = AccountStore.getPaymentIssuer(
        this.props.defaultPaymentMethodToken,
        creditCardsFiltered,
      );
      this.setState({
        cardToken: this.props.defaultPaymentMethodToken,
        issuer: newIssuer,
      });
    }

    if (
      prevProps.paymentCredentials !== this.props.paymentCredentials &&
      !this.state.cardToken
    ) {
      this.refreshCardToken();
    }

    if (prevProps.creditCards !== this.props.creditCards) {
      this.refreshCardToken();
    }
  }

  componentWillUnmount() {
    AccountStore.removeChangeListener(this.onAccountStoreChanged);
  }

  refreshCardToken() {
    const creditCardsFiltered = Util.filterExpiredCreditCards(
      this.props.creditCards,
    );

    const isSpecialToken =
      this.state.cardToken === AccountConstants.APPLE_PAY_TOKEN ||
      this.state.cardToken === AccountConstants.GOOGLE_PAY_TOKEN ||
      this.state.cardToken === AccountConstants.PAYROLL_TOKEN;

    const currentCardExists = creditCardsFiltered.find(
      (c) => c.id === this.state.cardToken,
    );

    if ((!isSpecialToken && !currentCardExists) || !this.state.cardToken) {
      const getDefaultCard = creditCardsFiltered.filter(
        (item) => item.isDefault,
      );
      let newCardToken =
        getDefaultCard.length > 0
          ? getDefaultCard[0].id
          : this.props.defaultPaymentMethodToken;

      if (!newCardToken && this.props.paymentCredentials?.applePayAvailable) {
        newCardToken = AccountConstants.APPLE_PAY_TOKEN;
      } else if (
        !newCardToken &&
        this.props.paymentCredentials?.googlePayAvailable
      ) {
        newCardToken = AccountConstants.GOOGLE_PAY_TOKEN;
      } else if (!newCardToken && this.props.payrollAvailable) {
        newCardToken = AccountConstants.PAYROLL_TOKEN;
      }

      const newIssuer = AccountStore.getPaymentIssuer(
        newCardToken,
        creditCardsFiltered,
      );

      this.setState({
        cardToken: newCardToken,
        issuer: newIssuer,
      });
    }
  }

  handleMenuVisibilityChange(visible: boolean, whichMenu: string) {
    switch (whichMenu) {
      case 'payment':
        this.setState({paymentMenuVisible: visible});
        break;
      case 'amount':
        this.setState({amountMenuVisible: visible});
        break;
      default:
        break;
    }
  }

  formatCurrency(value: number) {
    return Util.formatCurrency(this.props, value, AccountStore.getCurrency());
  }

  amountSelected(value: string) {
    this.setState({
      amount: +value,
      amountMenuVisible: false,
    });
  }

  cardSelected(value: string) {
    if (value === AccountConstants.ADD_PAYMENT_TOKEN) {
      this.props?.close();
    } else {
      this.setState({
        cardToken: value,
        issuer: AccountStore.getPaymentIssuer(value, this.props.creditCards),
        paymentMenuVisible: false,
      });
    }
  }

  async addClicked() {
    const accountId = AccountStore.getAccountId() || '';
    const error = this.validate();
    if (error) {
      alertError(error);
      return;
    }

    const externalError = await this.props.addFundsValidate?.();
    if (externalError) {
      this.props.addFundsFailed?.(externalError);
      return;
    }

    const {rewardProgramId, rewardsMinLoadAmount, close} = this.props;

    const source = 'Rewards Screen LoadWallet With Fund';
    const isRewardFlow =
      !!rewardProgramId && this.state.amount >= rewardsMinLoadAmount;

    let rewardFlowStarted = false;

    if (isRewardFlow) {
      try {
        await rewardsStartPolicyInBackground(
          accountId,
          rewardProgramId,
          source,
        );
        rewardFlowStarted = true;
      } catch (e) {
        CrashlyticsEvents.log(
          'Exception',
          'RewardsFundAccountTab:startReward',
          generateErrorMessage(e),
        );
        Events.Error.trackEvent(
          'Exception',
          'RewardsFundAccountTab:startReward',
          generateErrorMessage(e),
        );
      }
    }
    const onRatingPrompt = async () => {
      await AppRatingService.showPrompt();
    };

    const fundCloseAndNavigate = async () => {
      await close?.();
      if (rewardFlowStarted) {
        NavActions.navigate(AppRoutes.RewardsScreen, {
          refreshRewards: true,
          suppressRedirect: true,
        });
      }
    };

    const token = this.state.cardToken;

    const handleFundFailure = async (paymentError: any) => {
      if (isRewardFlow && rewardFlowStarted) {
        try {
          await rewardsCancelPolicyInBackground(
            accountId,
            rewardProgramId,
            source,
          );
        } catch (cancelError) {
          CrashlyticsEvents.log(
            'Exception',
            'RewardsFundAccountTab:cancelReward',
            generateErrorMessage(cancelError),
          );
          Events.Error.trackEvent(
            'Exception',
            'RewardsFundAccountTab:cancelReward',
            generateErrorMessage(cancelError),
          );
        }
      }

      this.props.addFundsFailed?.(paymentError);
    };

    try {
      if (token === AccountConstants.PAYROLL_TOKEN) {
        await FundingService.addFundsPayroll(
          this.state.amount,
          this.props.payrollIdentifierId,
          this.props.transDate,
          this.context,
          this.addFundsSuccess,
          handleFundFailure,
        );
      } else if (token === AccountConstants.APPLE_PAY_TOKEN) {
        this.props.dispatch(startPaymentFunding());
        await FundingService.addFundsApplePay(
          this.state.amount,
          this.context,
          handleFundFailure,
          this.addFundsSuccess,
        );
      } else if (token === AccountConstants.GOOGLE_PAY_TOKEN) {
        this.props.dispatch(startPaymentFunding());
        await FundingService.addFundsGooglePay(
          this.state.amount,
          this.context,
          this.addFundsSuccess,
          handleFundFailure,
        );
      } else {
        await this.props.addFundsCard({
          amount: this.state.amount,
          tokenId: this.state.cardToken,
          addFundSuccess: this.addFundsSuccess,
          addFundFailedCallback: handleFundFailure,
        });
      }

      await fundCloseAndNavigate();
      await onRatingPrompt();
    } catch (paymentError) {
      await handleFundFailure(paymentError);
    }
  }

  addFundsSuccess() {
    Events.AddFunds.trackEvent(
      FundStatus.Success,
      this.state.amount,
      this.state.cardToken,
      this.props.paymentCredentials.type,
    );


    this.props.dispatch(
      adjustDefaultBalance({
        amount: this.state.amount,
        reason: Localized.Labels.purchase,
      }),
    );

    if (this.props.close) {
      this.props.close();
    }

    if (this.props.addFundsSuccess) {
      setTimeout(() => {
        this.props.addFundsSuccess();
      }, 300);
    }
  }

  validate() {
    if (!this.state.cardToken || !this.state.amount) {
      return Localized.Errors.select_account_and_amount;
    } else if (
      this.state.amount !== this.props.remainingDue &&
      this.state.amount < AccountConstants.MINIMUM_FUNDING_AMOUNT
    ) {
      return `${Localized.Errors.minimum_amount_is} ${this.formatCurrency(
        AccountConstants.MINIMUM_FUNDING_AMOUNT,
      )}`;
    } else if (isNaN(this.state.amount)) {
      return Localized.Errors.invalid_amount;
    } else if (!this.hasSameCurrencyAsPayroll()) {
      return Localized.Errors.the_currencies_does_not_match;
    }

    return '';
  }

  hasSameCurrencyAsPayroll() {
    if (this.state.cardToken !== AccountConstants.PAYROLL_TOKEN) {
      return true;
    }
    const accountCurrency = AccountStore.getCurrency();

    const accountBalanceId = AccountStore.getAccountBalanceId();
    const targetAccountBalance =
      this.props.balances?.length > 0 &&
      this.props.balances.find((item) => item.id === accountBalanceId);

    const payroll =
      this.props.balances?.length > 0 &&
      this.props.balances.find(
        (item) =>
          item.type === BalanceTypes.Payroll && item.isDisabled === false,
      );

    if (payroll && payroll?.currency) {
      return targetAccountBalance?.currency === payroll?.currency;
    }

    return targetAccountBalance?.currency === accountCurrency;
  }

  onAccountStoreChanged() {
    const creditCardsFiltered = Util.filterExpiredCreditCards(
      this.props.creditCards,
    );

    const isSpecialToken =
      this.state.cardToken === AccountConstants.APPLE_PAY_TOKEN ||
      this.state.cardToken === AccountConstants.GOOGLE_PAY_TOKEN ||
      this.state.cardToken === AccountConstants.PAYROLL_TOKEN;

    const currentCardExists = creditCardsFiltered.find(
      (c) => c.id === this.state.cardToken,
    );

    let newCardToken = this.state.cardToken;
    if (!isSpecialToken && !currentCardExists) {
      const getDefaultCard = creditCardsFiltered.filter(
        (item) => item.isDefault,
      );
      newCardToken =
        getDefaultCard.length > 0
          ? getDefaultCard[0].id
          : this.props.defaultPaymentMethodToken;
    }

    const newIssuer = AccountStore.getPaymentIssuer(
      newCardToken,
      creditCardsFiltered,
    );

    this.setState({
      autoFundOn: AccountStore.isAutoFundingOn(),
      autoFundAmount: AccountStore.getAutoFundAmount(),
      autoFundBelowAmount: AccountStore.getAutoFundFallBelowAmount(),
      cardToken: newCardToken,
      issuer: newIssuer,
    });
  }

  renderAutoReloadSection() {
    if (Settings.isNewUI()) {
      return this.renderNewAutoReloadSection();
    } else {
      return this.renderOldAutoReloadSection();
    }
  }

  renderOldAutoReloadSection() {
    let summaryText: string | string[] = Localized.Labels.click_edit_to_turn_on;

    if (this.state.autoFundOn) {
      summaryText = Localized.Labels.formatString(
        Localized.Labels.when_balance_is_below,
        this.formatCurrency(this.state.autoFundAmount),
        this.formatCurrency(this.state.autoFundBelowAmount),
      );
    }
    return (
      <View style={styles.autoReloadContainer}>
        <View accessible={true} style={styles.autoReloadText}>
          <Text
            accessibilityLabel={`${Localized.Labels.auto_reload}, `}
            nativeID={`${Localized.Labels.auto_reload}`}
            aria-label={`${Localized.Labels.auto_reload}, `}
            accessibilityRole="text"
            maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm7}
            style={[
              {
                color: Styles.darkColor,
              },
              getSnackDescriber()?.snackDetailsBtnFontSTyle(),
            ]}
          >
            {Localized.Labels.auto_reload}
          </Text>
          <Text
            accessibilityLabel={summaryText.toString()}
            nativeID={summaryText.toString()}
            aria-label={summaryText.toString()}
            accessibilityRole="text"
            maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm7}
            style={[
              styles.reloadSummary,
              getSnackDescriber()?.snackDetailsBtnFontSTyle(),
            ]}
          >
            {summaryText}
          </Text>
        </View>
        <RoundedButton
          text={Localized.Buttons.edit}
          containerStyle={styles.autoLoadButton}
          textStyle={styles.editButtonText}
          onPress={() => {
            NavActions.navigate(AppRoutes.AutoReloadPanel);
          }}
          buttonType={ButtonType.outline}
          accessible={true}
          accessibilityLabel={Localized.Labels.edit_auto_reload}
          aria-label={Localized.Labels.edit_auto_reload}
          accessibilityHint="Double tap to set up or edit automatic reload"
          accessibilityRole="button"
          role="button"
          maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm4}
        />
      </View>
    );
  }

  renderNewAutoReloadSection() {
    const {fundAutoReloadTxtStyleDescriptor, getModalTexts} = getDescriber();
    return (
      <AVTouchableOpacity
        style={styles.touchableContainer}
        onPress={() => {
          NavActions.navigate(AppRoutes.AutoReloadPanel);
        }}
      >
        <View style={styles.autoReloadNewContainer}>
          <View
            accessible={true}
            style={styles.autoReloadText}
            testID="qa_lnk_EditAutoReload"
            nativeID="qa_lnk_EditAutoReload"
          >
            <Text
              accessibilityLabel={`${Localized.Labels.auto_reload}, `}
              nativeID={`${Localized.Labels.auto_reload}`}
              aria-label={`${Localized.Labels.auto_reload}, `}
              accessibilityRole="text"
              maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm4}
              style={[
                fundAutoReloadTxtStyleDescriptor(),
                getSnackDescriber()?.snackDetailsBtnFontSTyle(),
              ]}
            >
              {getModalTexts().fundModalReloadHeader}
            </Text>
          </View>
          <View style={{paddingEnd: Styles.Spacing.m4 - 8}}>
            {Platform.OS === 'web' ? (
              <FontAwesome5Pro
                accessibilityLabel="Right arrow icon"
                aria-label="Right arrow icon"
                name="chevron-right"
                color={Styles.lightGray}
                size={Styles.Fonts.f2}
                light
              />
            ) : (
              <ChevronRight />
            )}
          </View>
        </View>
      </AVTouchableOpacity>
    );
  }

  canAddFund = () => {
    const creditCardsFiltered = Util.filterExpiredCreditCards(
      this.props.creditCards,
    );

    // Determine if the currently selected payment method is expired
    const selectedCard = this.props.creditCards.find(
      (c) => c.id === this.state.cardToken,
    );
    const selectedCardIsExpired =
      selectedCard !== undefined && Util.isCreditCardExpired(selectedCard);

    const noValidOptions = !(
      this.props.paymentCredentials?.googlePayAvailable ||
      this.props.paymentCredentials?.applePayAvailable ||
      creditCardsFiltered.length > 0 ||
      this.props.payrollAvailable
    );

    const noSelection = !this.state.cardToken && noValidOptions;

    return noValidOptions || selectedCardIsExpired || noSelection;
  };

  checkisRefive = () => {
    return Settings.isRefive() ? '#3070ED' : null;
  };
  check365Color = () => {
    return Settings.is365Pay() ? '#066DFF' : this.checkisRefive();
  };

  checkColor = () => {
    return this.check365Color();
  };

  render() {
    let buttonText = `${Localized.Buttons.add} ${this.formatCurrency(
      this.state.amount,
    )}`;

    if (this.props.remainingDue) {
      buttonText = `${Localized.Buttons.checkout} ${this.formatCurrency(
        this.state.amount,
      )}`;
    }

    if (this.props.isSnackGiftDue) {
      buttonText = Localized.Buttons.fund_and_send_snack;
    }

    const creditCardsFiltered = Util.filterExpiredCreditCards(
      this.props.creditCards,
    );
    let title = Localized.Labels.quick_fund_your_account;

    if (this.props.remainingDue) {
      title = Localized.Labels.complete_checkout;
    }
    const {getFundAccountTabContainerStyleDescriptor} = getDescriber();
    return (
      <View
        style={[
          getFundAccountTabContainerStyleDescriptor().container,
          styles.borderStyle,
        ]}
      >
        <Text
          role="heading"
          accessible={true}
          accessibilityRole="header"
          accessibilityLabel="Add money to your account"
          nativeID="Add money to your account"
          maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm2}
          aria-label="Add money to your account"
          style={[
            styles.headerNewText,

            Settings.isRefiveAnd365Pay() && styles.refiveTitle,
            getEleDescriber().fontStyleSuccessModal(),
          ]}
          testID="qa_AddAmountTitle_txt"
        >
          {title}
        </Text>

        <View style={styles.amountView}>
          <View
            accessible={true}
            accessibilityLabel={Localized.Labels.amount_drop_down}
            aria-label={Localized.Labels.amount_drop_down}
            role="menu"
            accessibilityRole="menu"
            accessibilityHint="Select the amount to be added to your account"
            accessibilityValue={{
              text: this.formatCurrency(this.state.amount).toString(),
            }}
            accessibilityState={{expanded: this.state.amountMenuVisible}}
            style={styles.amountDropDownContainer}
            testID="qa_dpd_AddAmount"
            nativeID="qa_dpd_AddAmount"
          >
            <AmountDropdown
              anchorPosition="bottom"
              ascendingOrder={false}
              onSelect={this.amountSelected}
              onVisibilityChange={() =>
                this.handleMenuVisibilityChange(
                  !this.state.amountMenuVisible,
                  'amount',
                )
              }
              selectedValue={this.state.amount}
              formatNumber={this.formatCurrency}
              remainingDue={this.props.remainingDue}
              maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm4}
              dropdownTestID=" qa_AddAmountOptions_btn"
            />
          </View>
          <View style={styles.paymentContainer}>
            {Settings.isNewUI() ? (
              <View />
            ) : (
              <View
                accessibilityLabel={`${this.state.issuer} icon`}
                aria-label={`${this.state.issuer} icon`}
                accessibilityRole="image"
                role="img"
                style={styles.ccImage}
              >
                <CreditCardImage issuer={this.state.issuer} />
              </View>
            )}
            <View
              accessible={true}
              accessibilityLabel={Localized.Labels.payment_method_drop_down}
              aria-label={Localized.Labels.payment_method_drop_down}
              aria-valuetext={this.state.issuer}
              role="menu"
              accessibilityRole="menu"
              accessibilityHint="Select the payment method you would like to use"
              accessibilityValue={{text: this.state.issuer}}
              accessibilityState={{expanded: this.state.paymentMenuVisible}}
              style={styles.dropdown}
              testID="qa_dpd_Payment"
              nativeID="qa_dpd_Payment"
            >
              <PaymentMethodDropdown
                anchorPosition="bottom"
                onSelect={this.cardSelected}
                onVisibilityChange={() =>
                  this.handleMenuVisibilityChange(
                    !this.state.paymentMenuVisible,
                    'payment',
                  )
                }
                value={this.state.cardToken}
                cards={creditCardsFiltered}
                applePayAvailable={
                  this.props.paymentCredentials.applePayAvailable
                }
                googlePayAvailable={
                  this.props.paymentCredentials.googlePayAvailable
                }
                payrollAvailable={this.props.payrollAvailable}
                maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm4}
              />
            </View>
          </View>

          <View
            accessibilityElementsHidden={
              this.state.paymentMenuVisible || this.state.amountMenuVisible
            }
            importantForAccessibility={
              this.state.paymentMenuVisible || this.state.amountMenuVisible
                ? 'no-hide-descendants'
                : 'yes'
            }
          >
            {this.props.remainingDue === 0 && this.renderAutoReloadSection()}
          </View>
        </View>
        <View style={{marginTop: Settings.isNewUI() ? 85 : 60}}>
          {!Settings.isNewUI() && (
            <RoundedButton
              buttonType={ButtonType.outline}
              maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm4}
              containerStyle={[styles.balancesButton]}
              textStyle={Styles.Style.actionButtonText}
              accessible={true}
              accessibilityLabel={Localized.Buttons.view_balances}
              aria-label={Localized.Buttons.view_balances}
              accessibilityHint="Double tap to navigate to the balances screen"
              accessibilityRole="button"
              role="button"
              onPress={() => {
                NavActions.navigate(AppRoutes.Balances);
              }}
              text="Balances"
            />
          )}
          <RoundedButton
            buttonType={ButtonType.action}
            maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm4}
            containerStyle={[styles.balancesButton]}
            textStyle={[
              Styles.Style.actionButtonText,
              Settings.is365Pay() ? styles.payStyle : null,
            ]}
            accessibilityLabel={buttonText}
            aria-label={buttonText}
            addShadow={true}
            color={this.checkColor()}
            accessibilityHint="This amount can be modified with the drop down above"
            accessibilityRole="button"
            role="button"
            onPress={this.addClicked}
            text={buttonText}
            disabled={this.canAddFund()}
            testID={this.props.remainingDue ? 'qa_Checkout_Btn' : 'qa_btn_Add'}
          />
        </View>
      </View>
    );
  }
}

const styles = StyleSheet.create({
  autoLoadButton: {
    alignSelf: 'center',
  },
  autoReloadContainer: {
    flexDirection: 'row',
    marginLeft: Styles.Spacing.m3,
    marginTop: Styles.Spacing.m4,
  },
  autoReloadNewContainer: {
    flexDirection: 'row',
    paddingVertical: Styles.Spacing.m2,
    marginLeft: Styles.Spacing.m3,
    marginTop: Styles.Spacing.m3,
  },
  amountDropDownContainer: {
    marginLeft: Settings.isNewUI()
      ? Styles.Spacing.m0
      : Styles.Spacing.m4 + Styles.Spacing.m3,
    paddingHorizontal: Settings.isNewUI()
      ? Styles.Spacing.m3
      : Styles.Spacing.m0,
  },
  autoReloadText: {
    flex: 1,
  },
  ccImage: {
    justifyContent: 'center',
    marginTop: Styles.Spacing.m1,
    width: Styles.Spacing.m4 + Styles.Spacing.m3,
  },
  headerNewText: {
    color: Styles.darkColor,
    backgroundColor: 'transparent',
    fontWeight: '700',
    marginBottom: 20,
    marginLeft: Styles.Spacing.m3,
    fontSize: Settings.isNewUI() ? Styles.Fonts.sectionHeader : 0,
  },
  container: {
    ...(Platform.OS === 'web' && {
      flex: 1,
    }),
    backgroundColor: Styles.white,
    height: Settings.isNewUI() ? 450 : 360,
    paddingBottom: Styles.Spacing.m4,
    paddingTop: Settings.isNewUI() ? Styles.Spacing.m2 : Styles.Spacing.m1,
    paddingHorizontal: Styles.Spacing.m3,
    borderBottomLeftRadius: Settings.isNewUI() ? 10 : 0,
    borderBottomRightRadius: Settings.isNewUI() ? 10 : 0,
  },
  dropdown: {
    flex: 1,
  },
  paymentContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    marginTop: Settings.isNewUI() ? Styles.Spacing.m3 : Styles.Spacing.m0,
    paddingHorizontal: Settings.isNewUI()
      ? Styles.Spacing.m3
      : Styles.Spacing.m0,
  },
  reloadSummary: {
    color: Styles.lightGray,
    fontSize: Styles.Fonts.f0,
  },
  balancesButton: {
    bottom: Settings.isNewUI()
      ? Styles.Spacing.m3
      : Styles.Spacing.m2 + Styles.Spacing.m4,
  },
  editButtonText: {
    paddingHorizontal: Styles.Spacing.m2,
  },
  borderStyle: {
    borderBottomLeftRadius: Styles.Spacing.m2 + 2,
    borderBottomRightRadius: Styles.Spacing.m2 + 2,
  },
  touchableContainer: {
    backgroundColor: Styles.white,
    alignContent: 'center',
  },
  refiveTitle: {
    color: Styles.darkColor,
    fontWeight: '700',
    fontSize: Styles.Fonts.f9,
  },
  amountView: {
    borderBottomEndRadius: Styles.Spacing.m2 + 2,
    borderBottomStartRadius: Styles.Spacing.m2 + 2,
  },
  payStyle: {
    fontFamily: Styles.FontFamily.aeonikRegular,
    fontSize: Styles.Fonts.f1,
    fontWeight: '700',
  },
});

const ConnectedFundAccountTab = connect(
  (state: RootState) => ({
    paymentCredentials: state.account.paymentCredentials,
    creditCards: state.account.creditCards,
    isHomeLocationSma: state.account.isHomeLocationSma,
    defaultPaymentMethodToken: state.account.defaultPaymentToken,
    payrollAvailable: state.account.account.isPayrollAvailable,
    payrollIdentifierId: state.account.account.payrollIdentifier?.id,
    balances: state.account.account.balances,
  }),
  (dispatch: AppDispatch) => ({
    addFundsCard: (params: IAddFundsCardParams) =>
      dispatch(addFundsCard(params)),
    dispatch,
  }),
)(FundAccountTab);
type WithGlobalizeType = {
  transDate: string;
  remainingDue: number;
  isSnackGiftDue: boolean;
  rewardProgramId?: string;
  rewardsMinLoadAmount?: number;
  close: () => void;
  addFundsSuccess?: () => void;
  addFundsFailed?: () => void;
  addFundsValidate?: () => Promise<ErrorTypeEnum>;
} & WithGlobalizeProps;
export default withGlobalize<WithGlobalizeType>(ConnectedFundAccountTab);
